import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import MirakalousLogo from '../MirakalousLogo';
import GooeyNav from '../GooeyNav';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { 
      name: 'Solutions', 
      path: '/enterprise-ai-solutions-service-architecture',
      description: 'Enterprise AI Architecture'
    },
    { 
      name: 'Success Stories', 
      path: '/client-success-stories-quantified-results-gallery',
      description: 'Client Results & ROI'
    },
    { 
      name: 'Technology', 
      path: '/technology-stack-infrastructure',
      description: 'Infrastructure & Stack'
    },
    { 
      name: 'Security', 
      path: '/security-compliance-hub',
      description: 'Compliance & Security'
    },
    { 
      name: 'About', 
      path: '/about-ai-infrastructure-architects',
      description: 'Our Team & Expertise'
    },
    
  ];

  // GooeyNav items
  const gooeyNavItems = [
    { label: 'Solutions', href: '/enterprise-ai-solutions-service-architecture' },
    { label: 'Success Stories', href: '/client-success-stories-quantified-results-gallery' },
    { label: 'Technology', href: '/technology-stack-infrastructure' },
    { label: 'Security', href: '/security-compliance-hub' },
    { label: 'About', href: '/about-ai-infrastructure-architects' }
  ];


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[1000] enterprise-transition pointer-events-auto isolate ${
        isScrolled 
          ? 'bg-slate-950/90 backdrop-blur-xl backdrop-saturate-150 shadow-2xl border-b border-white/10' 
          : 'bg-slate-950/75 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <Link 
            to="/" 
            className="relative group flex items-center space-x-3 enterprise-transition"
            onClick={closeMenu}
            aria-label="Mirakalous — Home"
          >
            <span aria-hidden="true" className="absolute -inset-2 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-all duration-400" />
            <MirakalousLogo size={40} className="rounded-lg shadow-[0_0_0_2px_rgba(59,130,246,0.25)] group-hover:shadow-[0_0_0_3px_rgba(99,102,241,0.45)] enterprise-transition" useImage={true} />
            <div className="flex items-center space-x-2">
              <div className="flex flex-col">
                <span className="text-base md:text-lg font-extrabold leading-tight bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(59,130,246,0.35)]">
                  Mirakalous Technologies Inc.
                </span>
                <span className="text-xs font-semibold text-blue-300 leading-tight">
                  AI Solutions
                </span>
              </div>
              <Icon name="Sparkles" size={16} className="text-blue-300/90 group-hover:text-blue-200 enterprise-transition" />
            </div>
          </Link>

          {/* Desktop Navigation - GooeyNav */}
          <div className="hidden lg:flex items-center relative">
            <GooeyNav 
              items={gooeyNavItems}
              animationTime={500}
              particleCount={12}
              particleDistances={[70, 8]}
              particleR={80}
              timeVariance={200}
              colors={[1, 2, 3, 4]}
              initialActiveIndex={-1}
            />
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="default"
              size="sm"
              iconName="PhoneCall"
              onClick={() => window.location.href = '/contact-consultation-multi-tier-engagement'}
              className="relative rounded-full text-sm font-semibold text-white bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 shadow-[0_8px_30px_rgba(59,130,246,0.45)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.55)] ring-2 ring-white/10 hover:ring-white/30 px-5 enterprise-transition hover:scale-105"
            >
              Get Consultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 text-blue-300 hover:text-white enterprise-transition"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-slate-950/90 backdrop-blur-xl backdrop-saturate-150 border-t border-white/10">
            <div className="px-6 py-4 space-y-4">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`block py-2 text-base font-medium enterprise-transition ${
                    isActivePath(item?.path)
                      ? 'text-blue-300' :'text-white/80 hover:text-white'
                  }`}
                >
                  <div>{item?.name}</div>
                  <div className="text-sm text-blue-200/60 mt-0.5">
                    {item?.description}
                  </div>
                </Link>
              ))}
              
              <div className="border-t border-white/10 pt-4 mt-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="PhoneCall"
                  fullWidth
                  onClick={() => {
                    closeMenu();
                    window.location.href = '/contact-consultation-multi-tier-engagement';
                  }}
                  className="rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 text-white shadow-[0_8px_30px_rgba(59,130,246,0.45)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.55)]"
                >
                  Get Consultation
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;